/**
 * Route Registry - Single source of truth for application routes.
 *
 * This registry defines all routes with:
 * - Route configuration (path, label, flagKey)
 * - Permission control (allowedSources)
 * - Category grouping
 *
 * Used by:
 * - routes/index.tsx for React Router configuration
 * - Navigation domain for AI/user navigation coordination
 */

import React from 'react';

/**
 * Route definition type
 */
export interface RouteDefinition {
  /** Route path (matches React Router) */
  path: string;
  /** Human-readable label */
  label: string;
  /** Feature flag key that controls availability */
  flagKey: string;
  /** Who can navigate to this route */
  allowedSources: Array<'user' | 'ai' | 'url'>;
  /** Optional category for grouping */
  category?: 'analytics' | 'catalog' | 'operations' | 'settings' | 'finance' | 'communication' | 'sensitive';
  /**
   * When true, this route is appended to `HOME_REDIRECT_ORDER` as a last-resort
   * landing ( after every primary route ). Lets an otherwise-excluded route
   * ( e.g. a `sensitive` one ) still win the home redirect when no primary route
   * matches — used for the /subscription pay surface so a de-entitled / suspended
   * account lands somewhere it can restore entitlement. [TV1-2077]
   */
  homeRedirectFallback?: boolean;
  /** Component to render (lazy or direct) */
  component?: React.LazyExoticComponent<React.ComponentType> | React.ComponentType;
  /** Child routes (for tabbed views) */
  children?: RouteDefinition[];
}

/**
 * All application routes.
 *
 * IMPORTANT: When adding new routes, add them here first.
 * routes/index.tsx consumes this registry.
 */
export const ROUTE_REGISTRY: RouteDefinition[] = [
  // Dashboard & Reports (AI-friendly)
  {
    path: '/dashboard',
    label: 'Dashboard',
    flagKey: 'dashboard',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'analytics'
  },
  {
    path: '/reports',
    label: 'Reports',
    flagKey: 'reports',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'analytics'
  },

  // Inventory (AI-friendly)
  {
    path: '/inventory',
    label: 'Inventory',
    flagKey: 'inventory',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },
  {
    path: '/inventory/:tabId',
    label: 'Inventory Tab',
    flagKey: 'inventory',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },
  {
    path: '/inventory/list/view/:itemId',
    label: 'View Item',
    flagKey: 'inventory',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },
  {
    path: '/inventory/list/edit/:itemId',
    label: 'Edit Item',
    flagKey: 'inventory',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },

  // Publications (AI-friendly)
  {
    path: '/publications',
    label: 'Publications',
    flagKey: 'publications',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },
  {
    path: '/publications/:tabId',
    label: 'Publications Tab',
    flagKey: 'publications',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },
  {
    path: '/publications/list/view/:publicationId',
    label: 'View Publication',
    flagKey: 'publications',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },
  {
    path: '/publications/list/edit/:publicationId',
    label: 'Edit Publication',
    flagKey: 'publications',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'catalog'
  },

  // Warehouses (AI-friendly)
  {
    path: '/warehouses',
    label: 'Warehouses',
    flagKey: 'warehouses',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'operations'
  },

  // Suppliers (AI-friendly) — TV1-2158 (S7). The AI may navigate here and fill
  // the supplier form; `procurement/state/invariants.ts` denies it submit,
  // data/* and the whole deactivate/reactivate family.
  {
    path: '/suppliers',
    label: 'Suppliers',
    flagKey: 'suppliers',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'operations'
  },

  // Orders (AI-friendly)
  {
    path: '/orders',
    label: 'Orders',
    flagKey: 'orders',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'operations'
  },
  {
    path: '/orders/:tabId',
    label: 'Orders Tab',
    flagKey: 'orders',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'operations'
  },

  // Integrations (AI-friendly)
  {
    path: '/integrations',
    label: 'Integrations',
    flagKey: 'integrations',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'settings'
  },
  {
    path: '/integrations/:tabId',
    label: 'Integrations Tab',
    flagKey: 'integrations',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'settings'
  },
  {
    path: '/integrations/channels/:integrationId',
    label: 'Channel Info',
    flagKey: 'integrations',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'settings'
  },
  {
    path: '/integrations/erps/:erpId',
    label: 'ERP Info',
    flagKey: 'integrations',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'settings'
  },

  // Accounting (AI-friendly)
  {
    path: '/accounting',
    label: 'Accounting',
    flagKey: 'accounting',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'finance'
  },
  {
    path: '/accounting/:tabId',
    label: 'Accounting Tab',
    flagKey: 'accounting',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'finance'
  },

  // Interactions (AI-friendly)
  {
    path: '/interactions',
    label: 'Interactions',
    flagKey: 'interactions',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'communication'
  },
  {
    path: '/interactions/:tabId',
    label: 'Interactions Tab',
    flagKey: 'interactions',
    allowedSources: [ 'user', 'ai', 'url' ],
    category: 'communication'
  },

  // Notifications (User only - AI can see but not navigate)
  {
    path: '/notifications',
    label: 'Notifications',
    flagKey: 'notifications',
    allowedSources: [ 'user', 'url' ],
    category: 'communication'
  },
  {
    path: '/notifications/:tabId',
    label: 'Notifications Tab',
    flagKey: 'notifications',
    allowedSources: [ 'user', 'url' ],
    category: 'communication'
  },

  // Batch Processes (User/URL only)
  {
    path: '/batch-processes',
    label: 'Batch Processes',
    flagKey: 'batch-processes-view',
    allowedSources: [ 'user', 'url' ],
    category: 'operations'
  },
  {
    path: '/batch-processes/:processId',
    label: 'Batch Process Detail',
    flagKey: 'batch-processes-view',
    allowedSources: [ 'user', 'url' ],
    category: 'operations'
  },

  // Operations review surface (TV1-1828, User/URL only — reached by URL after a
  // bulk-action propose; AI bulk-apply is gated separately and out of scope here)
  {
    path: '/operations',
    label: 'Operations',
    flagKey: 'operations',
    allowedSources: [ 'user', 'url' ],
    category: 'operations'
  },
  {
    path: '/operations/review/:runId',
    label: 'Operation Review',
    flagKey: 'operations',
    allowedSources: [ 'user', 'url' ],
    category: 'operations'
  },

  // SENSITIVE ROUTES - AI BLOCKED
  {
    path: '/accountSettings',
    label: 'Account Settings',
    flagKey: 'account',
    allowedSources: [ 'user', 'url' ], // ❌ AI cannot access
    category: 'sensitive'
  },
  {
    path: '/accountSettings/:tabId',
    label: 'Account Settings Tab',
    flagKey: 'account',
    allowedSources: [ 'user', 'url' ],
    category: 'sensitive'
  },
  {
    path: '/subscription',
    label: 'Subscription',
    flagKey: 'subscription',
    allowedSources: [ 'user', 'url' ], // ❌ AI cannot access billing
    category: 'sensitive',
    // Last-resort home landing for a de-entitled / suspended account whose flags
    // have collapsed to the billing surface only ( TV1-2077 slice 1 ) — the real
    // pay surface where they can restore entitlement. Ranked last, so a fully
    // entitled user still lands on their first primary route. /billing is a stub
    // and is deliberately NOT a fallback.
    homeRedirectFallback: true
  },
  {
    path: '/subscription/:tabId',
    label: 'Subscription Tab',
    flagKey: 'subscription',
    allowedSources: [ 'user', 'url' ],
    category: 'sensitive'
  },
  {
    path: '/billing',
    label: 'Billing',
    flagKey: 'billing',
    allowedSources: [ 'user', 'url' ], // ❌ AI cannot access billing
    category: 'sensitive'
  }

];

/**
 * Get home redirect order (first enabled route becomes home).
 *
 * Primary routes come first (non-parameterized, non-sensitive). A de-entitled /
 * suspended account (TV1-2077 slice 1) collapses its feature flags to the billing
 * surface only, so NONE of the primary routes match and the user would otherwise
 * fall through to /not-found. Routes flagged `homeRedirectFallback` are appended
 * LAST as a pay-surface fallback (/subscription), so such a user reaches a page
 * where they can restore entitlement — while a fully entitled user still lands on
 * their first primary route.
 */
const HOME_REDIRECT_PRIMARY = ROUTE_REGISTRY
  .filter( route => !route.path.includes( ':' )) // Exclude parameterized routes
  .filter( route => route.category !== 'sensitive' ); // Exclude sensitive routes from home redirect

const HOME_REDIRECT_FALLBACK = ROUTE_REGISTRY
  .filter( route => !route.path.includes( ':' )) // Exclude parameterized routes
  .filter( route => route.homeRedirectFallback )
  .filter( route => !HOME_REDIRECT_PRIMARY.includes( route )); // Never double-list a primary route

export const HOME_REDIRECT_ORDER = [ ...HOME_REDIRECT_PRIMARY, ...HOME_REDIRECT_FALLBACK ]
  .map( route => ({ path: route.path, flagKey: route.flagKey }));

/**
 * Find route definition by path
 */
export const findRouteByPath = ( path: string ): RouteDefinition | undefined => {
  return ROUTE_REGISTRY.find( route => route.path === path );
};

/**
 * Check if a source can navigate to a path
 */
export const canSourceNavigate = (
  path: string,
  source: 'user' | 'ai' | 'url'
): boolean => {
  const route = findRouteByPath( path );
  if ( !route ) return false;
  return route.allowedSources.includes( source );
};

/**
 * Get routes accessible to AI
 */
export const getAIAccessibleRoutes = (): RouteDefinition[] => {
  return ROUTE_REGISTRY.filter( route => route.allowedSources.includes( 'ai' ));
};

/**
 * Get routes by category
 */
export const getRoutesByCategory = (
  category: RouteDefinition['category']
): RouteDefinition[] => {
  return ROUTE_REGISTRY.filter( route => route.category === category );
};
